<script>
    import { getContext } from 'svelte';
    import { Policy } from "@Store";
    import Question from "@components/Question.svelte";
    import 'animate.css';
    import { computePriorInsuranceAndClaimsValid } from '@routes/validators/validator';
    const CONTEXT = getContext('CONTEXT');

    $: questions = $Policy.questions ? $Policy.questions : [];

    // APPLICANT QUESTIONS SECTION

    // Create a lookup map in one pass - much more efficient!
    $: questionMap = questions.reduce((map, question) => {
        map[question.question] = question;
        return map;
    }, {});

    // Now you can access any question efficiently
    $: ({
        'Have any of your policies ever been declined, cancelled or non-renewed?': prior_insurance_and_claims_question,
        'Provide details': provide_details_question,
        'Have any of your policies had any claims in the last 5 years?': claims_in_the_last_5_years_question,
        'Please provide the description of claim, including the date of loss and payout amount': claim_description_question,
        'I would like to request higher or different limits for one or more of the coverages selected in this application': request_higher_or_different_limits_question,
        'Please Share Any Additional Details, Clarifications, or Requests Here:': additional_details_question,
    } = questionMap);

    // Create named values lookup map - ONE iteration instead of 9!
    $: namedValuesMap = ($Policy.saved_named_values || []).reduce((map, nv) => {
        map[nv.name] = nv.value?.trim() || '';
        return map;
    }, {});

    // Helper function to check if a named value exists and is not empty
    $: getAnswerText = (q) => (q && $Policy.answers?.[q.id]?.text_answer || '').trim();
    $: isAnsweredById = (q) => !!getAnswerText(q);

    // Now efficiently check all values
    $: prior_insurance_and_claims_question_answered = isAnsweredById(prior_insurance_and_claims_question);
    $: show_details = prior_insurance_and_claims_question_answered && getAnswerText(prior_insurance_and_claims_question).toLowerCase().includes('yes');
    $: claims_in_the_last_5_years = namedValuesMap['has_claims']?.toLowerCase().includes('yes');

    $: prior_insurance_and_claims_valid = computePriorInsuranceAndClaimsValid($Policy);
    // Update CONTEXT when validation changes
    $: {
        CONTEXT.routeValidation.update(validation => ({
            ...validation,
            priorinsuranceandclaims: prior_insurance_and_claims_valid
        }));
    }

    function handleAnswer(e) {
        CONTEXT.handleAnswer(e);
    }


</script>

<div class="page-container">
    <div class="questions-wrapper">
        <!-- APPLICANT QUESTIONS -->
        {#if prior_insurance_and_claims_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={prior_insurance_and_claims_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
            {#if prior_insurance_and_claims_question_answered && show_details}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={provide_details_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
        {/if}
        {#if claims_in_the_last_5_years_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={claims_in_the_last_5_years_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
            {#if claims_in_the_last_5_years}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={claim_description_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
        {/if}
        {#if request_higher_or_different_limits_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={request_higher_or_different_limits_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}
        {#if additional_details_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={additional_details_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}
    </div>
</div>

<style>
    .page-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 1rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .questions-wrapper {
        width: 100%;
        max-width: 500px;
        margin-bottom: 2rem;
    }

    /* Section Container Styles */
    .section-container {
        width: 100%;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        box-sizing: border-box;
        padding: 2rem 1.5rem;
        margin: 2rem 0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    }

    .section-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding-bottom: 1.5rem;
        border-bottom: 2px solid #e9ecef;
    }

    .section-icon {
        font-size: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
        border-radius: 16px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
    }

    .section-header h1 {
        font-size: 1.375rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0;
        line-height: 1.3;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .sub-questions {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .sub-question {
        position: relative;
    }

    @media (max-width: 768px) {
        .section-container {
            padding: 1.5rem 1rem;
            border-radius: 16px;
        }

        .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }

        .section-header h1 {
            font-size: 1.25rem;
        }

        .section-icon {
            width: 48px;
            height: 48px;
            font-size: 1.75rem;
        }

        .sub-question {
            padding-left: 1rem;
        }

        .sub-question::before {
            width: 3px;
        }
    }
</style>